import { Menu, MenuPopover } from '@vizit/atoms';
import { ContextMenu } from '@vizit/atoms';
import { cx } from '@vizit/theme';
import React from 'react';
import { AssetContext } from './asset.context-provider';
import { ContextMenuMode, contextMenuTriggerClasses } from './context-menu-trigger-classes';

interface AssetContextMenuProps extends React.ComponentPropsWithoutRef<typeof Menu> {
  /**
   * Visual mode for the trigger button
   * - light: default white background
   * - dark: dark background with white icon (for use on asset cards)
   */
  mode?: ContextMenuMode;
}

function AssetContextMenu(props: AssetContextMenuProps) {
  const { children, className, mode = ContextMenuMode.Dark, ...other } = props;
  const { isHovered, isFocused, isLoading, isViewOnly, onContextMenuMount, onHoverChange } =
    React.useContext(AssetContext);
  const [isMenuOpen, setIsMenuOpen] = React.useState(false);

  const handleOpenChange = React.useCallback(
    (isOpen: boolean) => {
      setIsMenuOpen(isOpen);
      if (!isOpen) onHoverChange?.(false);
    },
    [onHoverChange],
  );

  React.useEffect(() => {
    if (isViewOnly) return;
    onContextMenuMount?.(true);

    return () => {
      onContextMenuMount?.(false);
    };
  }, [isViewOnly, onContextMenuMount]);

  if (isViewOnly) return null;

  return (
    <ContextMenu
      onOpenChange={handleOpenChange}
      triggerProps={{
        isHovered: isHovered || isFocused,
        isMenuOpen,
        isLoading,
        className: cx(contextMenuTriggerClasses({ mode }), 'absolute right-[7px]', className),
        style: { top: 'var(--asset-button-top, 7px)' },
      }}
    >
      <MenuPopover>
        <Menu {...other}>{children}</Menu>
      </MenuPopover>
    </ContextMenu>
  );
}

export { AssetContextMenu };
